'use client'

import React, { useState } from 'react'

import { Box, Square } from '@chakra-ui/react'

import { BadgeCounter } from '../BadgeCounter'
import { Tooltip } from '../Tooltip/Tooltip'
import {
  ColorScheme,
  SIDE_NAV_WIDTH_CLOSED,
  sideNavIconStyles,
  sideNavItemWrapperStyles,
  sideNavLabelStyles,
} from './SideNavItem.styles'

export type SideNavItemProps = {
  icon: React.ElementType
  label: string
  isActive: boolean
  isExpanded: boolean
  badge?: React.ReactNode
  iconSize?: string
  colorScheme?: ColorScheme
  'data-testid'?: string
  onClick?: () => void
}

export const SideNavItem = ({
  icon,
  label,
  isActive,
  isExpanded,
  badge,
  iconSize = '24px',
  colorScheme = 'dark',
  'data-testid': dataTestId,
  onClick,
}: SideNavItemProps) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)

  return (
    <Box
      {...sideNavItemWrapperStyles(isActive, isExpanded, colorScheme)}
      data-testid={dataTestId}
      onClick={onClick}
    >
      <Square
        h='60px'
        onMouseEnter={(e) => !isExpanded && setAnchorEl(e.currentTarget)}
        onMouseLeave={() => setAnchorEl(null)}
        position='relative'
        w={SIDE_NAV_WIDTH_CLOSED}
      >
        {badge && !isExpanded && (
          <Box lineHeight={0} position='absolute' right='10px' top='12px'>
            <BadgeCounter
              colorScheme={colorScheme}
              variant='notification-dot'
            />
          </Box>
        )}
        <Box
          alignItems='center'
          // Constrain the rendered SVG to iconSize regardless of the icon's own
          // width/height attributes. SVG preserveAspectRatio keeps non-square
          // icons proportional (letterboxed) rather than stretched.
          css={{ '& svg': { width: iconSize, height: iconSize } }}
          display='flex'
          flexShrink={0}
          h={iconSize}
          justifyContent='center'
          w={iconSize}
          {...sideNavIconStyles(isActive, colorScheme)}
        >
          {React.createElement(icon)}
        </Box>
      </Square>

      {!isExpanded && (
        <Tooltip
          anchorEl={anchorEl}
          colorScheme={colorScheme}
          label={label}
          placement='right'
        />
      )}

      {isExpanded && (
        <Box alignItems='center' display='flex' gap='8px' overflow='hidden'>
          <Box {...sideNavLabelStyles(isActive, colorScheme)}>{label}</Box>
          {badge}
        </Box>
      )}
    </Box>
  )
}
